import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '../utils/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060A10] flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Logo */}
        <div className="flex items-center gap-3 justify-center">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/df6b9931-f35a-4f03-9adf-4a4572c904c3/images/6dfa2d55642f0060455034e074f9f811d7710ca6e7452f73c1fd5e309a79f2ae.png"
            alt="TradeForge"
            className="w-12 h-12"
          />
          <span className="text-3xl font-bold font-['Manrope'] bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            TradeForge
          </span>
        </div>

        {/* Card */}
        <div className="bg-[#0D131C] border border-[#1E293B] rounded-xl p-8">
          {emailSent ? (
            /* Success state */
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00E676]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-[#00E676]" />
              </div>
              <h2 className="text-2xl font-bold font-['Manrope'] text-white mb-2">Check Your Email</h2>
              <p className="text-slate-400 mb-6">
                We've sent a password reset link to<br />
                <span className="text-cyan-400 font-medium">{email}</span>
              </p>
              <div className="bg-[#060A10] border border-[#1E293B] rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-400">
                  Didn't receive the email? Check your spam folder or
                  <button 
                    onClick={() => setEmailSent(false)} 
                    className="text-cyan-400 hover:text-cyan-300 ml-1"
                  >
                    try again
                  </button>
                </p>
              </div>
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          ) : (
            /* Form state */
            <>
              <h2 className="text-2xl font-bold font-['Manrope'] text-white text-center mb-2">
                Forgot Password?
              </h2>
              <p className="text-slate-400 text-center mb-8">
                Enter your email and we'll send you a reset link
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="email" className="text-slate-300 text-sm font-mono uppercase tracking-wider">
                    Email Address
                  </Label>
                  <div className="mt-2 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-[#060A10] border-[#1E293B] focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-white h-11 rounded-lg"
                      placeholder="you@example.com"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold h-11 rounded-lg"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link 
                  to="/login" 
                  className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
